/** The partner page's two edit forms (react-hook-form + zod):
 *  - identity: name, legal name, kind, notes;
 *  - contract: reference, signing date, signatory, term end, and the signed PDF.
 *  "Recorded" needs all five; the PDF is checked by the server (PDF bytes only). */
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  downloadContract,
  PARTNER_KIND_LABELS,
  update,
  uploadContract,
  type PartnerDetail,
  type PartnerKind,
  type PartnerUpdate,
} from '@/api/partners';
import { useSnackbar } from '@/components/Snackbar';

import { partnerErrorMessage } from './partnerErrors';

const KINDS = Object.keys(PARTNER_KIND_LABELS) as [PartnerKind, ...PartnerKind[]];

// ---------------------------------------------------------------------------- identity

const IdentitySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  legal_name: z.string().trim().max(255),
  kind: z.enum(KINDS),
  notes: z.string(),
});
type IdentityValues = z.infer<typeof IdentitySchema>;

function useSave(partnerId: number, done: string) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: (body: PartnerUpdate) => update(partnerId, body),
    onSuccess: () => {
      snackbar.show(done, 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
    },
    onError: (e) => snackbar.show(partnerErrorMessage(e), 'error'),
  });
}

export function PartnerIdentityForm({ partner, canManage }: { partner: PartnerDetail; canManage: boolean }) {
  const save = useSave(partner.id, 'Partner saved.');
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IdentityValues>({ resolver: zodResolver(IdentitySchema) });

  useEffect(() => {
    reset({ name: partner.name, legal_name: partner.legal_name, kind: partner.kind, notes: partner.notes });
  }, [partner.name, partner.legal_name, partner.kind, partner.notes, reset]);

  return (
    <Box component="form" onSubmit={handleSubmit((v) => save.mutate(v))} sx={{ display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          label="Name"
          required
          disabled={!canManage}
          {...register('name')}
          error={!!errors.name}
          helperText={errors.name?.message}
        />
        <TextField
          label="Legal name"
          disabled={!canManage}
          {...register('legal_name')}
          helperText="The contracting entity."
        />
        <Controller
          name="kind"
          control={control}
          render={({ field }) => (
            <TextField select label="Kind" disabled={!canManage} {...field} value={field.value ?? partner.kind}>
              {KINDS.map((k) => (
                <MenuItem key={k} value={k}>
                  {PARTNER_KIND_LABELS[k]}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <TextField label="Code" value={partner.code} disabled helperText="Permanent: it names the sandbox." />
      </Box>
      <TextField label="Notes" multiline minRows={2} disabled={!canManage} {...register('notes')} />
      {canManage && (
        <Box>
          <Button type="submit" variant="contained" disabled={!isDirty || save.isPending}>
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------- contract

const ContractSchema = z
  .object({
    contract_reference: z.string().trim().max(255),
    contract_signed_on: z.string(),
    contract_signatory: z.string().trim().max(255),
    contract_term_end: z.string(),
  })
  .refine(
    (v) => !v.contract_signed_on || !v.contract_term_end || v.contract_term_end >= v.contract_signed_on,
    { path: ['contract_term_end'], message: 'The term cannot end before the contract was signed.' },
  );
type ContractValues = z.infer<typeof ContractSchema>;

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function PartnerContractForm({ partner, canManage }: { partner: PartnerDetail; canManage: boolean }) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const save = useSave(partner.id, 'Contract saved.');
  const fileInput = useRef<HTMLInputElement>(null);
  const c = partner.contract;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ContractValues>({ resolver: zodResolver(ContractSchema) });

  useEffect(() => {
    reset({
      contract_reference: c.reference,
      contract_signed_on: c.signed_on ?? '',
      contract_signatory: c.signatory,
      contract_term_end: c.term_end ?? '',
    });
  }, [c.reference, c.signed_on, c.signatory, c.term_end, reset]);

  const upload = useMutation({
    mutationFn: (file: File) => uploadContract(partner.id, file),
    onSuccess: () => {
      snackbar.show('Signed contract stored.', 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
    },
    onError: (e) => snackbar.show(partnerErrorMessage(e, 'Upload failed.'), 'error'),
  });

  const download = useMutation({
    mutationFn: () => downloadContract(partner.id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${partner.code}-contract.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (e) => snackbar.show(partnerErrorMessage(e, 'Download failed.'), 'error'),
  });

  const submit = handleSubmit((v) =>
    save.mutate({
      contract_reference: v.contract_reference,
      contract_signatory: v.contract_signatory,
      contract_signed_on: v.contract_signed_on || null,
      contract_term_end: v.contract_term_end || null,
    }),
  );

  return (
    <Stack spacing={2}>
      {c.recorded ? (
        <Alert severity="success">The contract is recorded.</Alert>
      ) : (
        <Alert severity="warning">
          Not recorded yet — missing {c.missing.join(', ')}. Until it is, the
          partner cannot hold a developer key and its sandbox setup waits.
        </Alert>
      )}
      <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField label="Reference" disabled={!canManage} {...register('contract_reference')} />
          <TextField
            label="Signatory"
            disabled={!canManage}
            {...register('contract_signatory')}
            helperText="Who signed for the partner."
          />
          <TextField
            label="Signed on"
            type="date"
            disabled={!canManage}
            InputLabelProps={{ shrink: true }}
            {...register('contract_signed_on')}
          />
          <TextField
            label="Term ends"
            type="date"
            disabled={!canManage}
            InputLabelProps={{ shrink: true }}
            {...register('contract_term_end')}
            error={!!errors.contract_term_end}
            helperText={errors.contract_term_end?.message ?? 'Developer keys are refused after this day.'}
          />
        </Box>
        {canManage && (
          <Box>
            <Button type="submit" variant="contained" disabled={!isDirty || save.isPending}>
              Save contract
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Signed PDF
        </Typography>
        {c.document ? (
          <>
            <Chip size="small" variant="outlined" label={`${c.document.name} · ${formatBytes(c.document.size)}`} />
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              disabled={download.isPending}
              onClick={() => download.mutate()}
            >
              Download
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            None stored
          </Typography>
        )}
        {canManage && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {c.document ? 'Replace PDF' : 'Upload PDF'}
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              data-testid="contract-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = '';
              }}
            />
          </>
        )}
      </Box>
    </Stack>
  );
}
